import React from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

import { AdvanceRateRouter } from './AdvanceRate';
import { ArrangmentGroupsRoutes } from './ArrangmentGroups';
import { AssignmentRouter } from './Assignment';
import { CustomersRouter } from './Customers';
import { LoanAccountsRouter } from './LoanAccounts';
import { SettingsRouter } from './settings/Router';
import { DefaultErrorRoute } from 'core/DefaultErrorRoute';
import DataMetadataProvider from 'valleyBank/domains/page/hooks/DataMetadataContext';
import UserRoleTypePremissionsProvider, {
  useUserRoleTypePermissionsContext,
} from 'valleyBank/domains/page/hooks/useUserRoleTypePermissions';
import {
  RouteErrorBoundary,
  RouteErrorBoundaryFallbackFn,
} from '~/contexts/MonitoringProvider';
import { LogoutOnUserInactivity } from '~/contexts/UserAuthProvider';

// Helpers
const defaultRouteErrorFallback: RouteErrorBoundaryFallbackFn = ({
  message,
  showErrorToast,
}) => {
  showErrorToast(message);
  return <Navigate to="/valley-bank/loan-accounts" />;
};

// Fragments
const ValleyBankInnerRouter = () => {
  const { permissions } = useUserRoleTypePermissionsContext();
  return (
    /**
     * Although the DataMetadataProvider is not used for the settings + advance-rate route, need to keep it at top level because the ValleyBankTemplate is shared across all routes
     */
    <DataMetadataProvider permissions={permissions}>
      <Routes>
        <Route path="/valley-bank">
          <Route
            path="loan-accounts/*"
            element={
              <RouteErrorBoundary
                resetAfterCatch={false}
                fallback={({ message, showErrorToast }) => {
                  showErrorToast(message);
                  return <DefaultErrorRoute />;
                }}
              >
                <LoanAccountsRouter />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="customers/*"
            element={
              <RouteErrorBoundary fallback={defaultRouteErrorFallback}>
                <CustomersRouter />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="arrangement-groups"
            element={
              <RouteErrorBoundary fallback={defaultRouteErrorFallback}>
                <ArrangmentGroupsRoutes />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="assignment"
            element={
              <RouteErrorBoundary fallback={defaultRouteErrorFallback}>
                <AssignmentRouter />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="advance-rate"
            element={
              <RouteErrorBoundary fallback={defaultRouteErrorFallback}>
                <AdvanceRateRouter />
              </RouteErrorBoundary>
            }
          />
        </Route>
        <Route
          path="/settings/*"
          element={
            <RouteErrorBoundary fallback={defaultRouteErrorFallback}>
              <SettingsRouter />
            </RouteErrorBoundary>
          }
        />
        <Route
          path="/*"
          element={<Navigate to="/valley-bank/loan-accounts" />}
        />
      </Routes>
    </DataMetadataProvider>
  );
};

export const ValleyBankRouter = () => {
  return (
    <LogoutOnUserInactivity>
      <UserRoleTypePremissionsProvider>
        <ValleyBankInnerRouter />
      </UserRoleTypePremissionsProvider>
    </LogoutOnUserInactivity>
  );
};
